import { pluralize } from '../../lib/pluralize';
import type {
    GroupOperationNounForms,
    GroupOperationPhase,
    IGroupOperationItem,
    GroupOperationItemStatus,
} from './GroupOperationDialog.types';

export const DEFAULT_NOUN_FORMS: GroupOperationNounForms = ['объект', 'объекта', 'объектов'];

export interface IGroupOperationCounts {
    total: number;
    /** Выполнится (ok + warning) */
    runnable: number;
    warning: number;
    blocked: number;
    success: number;
    failed: number;
}

export const countGroupOperation = (items: IGroupOperationItem[]): IGroupOperationCounts => {
    const count = (status: GroupOperationItemStatus) => items.filter((item) => item.status === status).length;
    const warning = count('warning');
    return {
        total: items.length,
        runnable: count('ok') + warning,
        warning,
        blocked: count('blocked'),
        success: count('success'),
        failed: count('failed'),
    };
};

/** Неудачи и отказы — наверх: ради них окно и открыто. Внутри группы — порядок выбора. */
const STATUS_ORDER: Record<GroupOperationItemStatus, number> = {
    failed: 0,
    blocked: 1,
    warning: 2,
    ok: 3,
    success: 3,
};

export const sortGroupOperationItems = (items: IGroupOperationItem[]): IGroupOperationItem[] =>
    items
        .map((item, index) => ({ item, index }))
        .sort((a, b) => STATUS_ORDER[a.item.status] - STATUS_ORDER[b.item.status] || a.index - b.index)
        .map(({ item }) => item);

export type GroupOperationFilter = 'all' | 'problems' | 'done';

/**
 * Предпросмотр: проблемные — нельзя и с предупреждением, проходящие — остальные. Итог: неудачные и выполненные;
 * не запускавшиеся видны только во «Всех» — у них своя метка в сводке.
 */
const FILTER_STATUSES: Record<
    GroupOperationPhase,
    Record<Exclude<GroupOperationFilter, 'all'>, GroupOperationItemStatus[]>
> = {
    loading: { problems: [], done: [] },
    preview: { problems: ['blocked', 'warning'], done: ['ok'] },
    result: { problems: ['failed'], done: ['success'] },
};

export const filterGroupOperationItems = (
    items: IGroupOperationItem[],
    filter: GroupOperationFilter,
    phase: GroupOperationPhase,
) => (filter === 'all' ? items : items.filter((item) => FILTER_STATUSES[phase][filter].includes(item.status)));

export const itemTitle = (item: IGroupOperationItem) => item.title?.trim() || `Объект ${item.id}`;

export const countWithNoun = (count: number, forms: GroupOperationNounForms) =>
    `${count.toLocaleString('ru-RU')} ${pluralize(count, forms)}`;

const STATUS_TEXT: Record<GroupOperationItemStatus, string> = {
    ok: 'Выполнится',
    warning: 'Выполнится с предупреждением',
    blocked: 'Нельзя',
    success: 'Выполнено',
    failed: 'Не выполнено',
};

export const statusText = (status: GroupOperationItemStatus, phase: GroupOperationPhase) =>
    phase === 'result' && status === 'blocked' ? 'Не запускалось' : STATUS_TEXT[status];

/** Текст для буфера обмена: строка на объект — статус, подпись, причины, код обращения. */
export const groupOperationCopyText = (
    actionName: string,
    items: IGroupOperationItem[],
    phase: GroupOperationPhase,
): string => {
    const lines = sortGroupOperationItems(items).map((item) => {
        const details = [...(item.messages ?? []), item.traceId ? `код обращения ${item.traceId}` : '']
            .filter(Boolean)
            .join('; ');
        return `${statusText(item.status, phase)}\t${itemTitle(item)}${details ? `\t${details}` : ''}`;
    });
    return [actionName, ...lines].join('\n');
};
